//================================================================
//  Component: Digital SME Initiative Live
//================================================================

//  Purpose: This is the digital SME initiative live field component to be used for live checkbox updates

//  Properties:
//    - initiativeCategories = {An array, this is the list of initiative categories}
//    - collectionid = {A string, this is the collection id}
//    - fieldpath = {A string, this is the field path}
//    - documentid = {A string, this is the document id}
//    - disabled = {A boolean, this is the disabled state}

//  Example:
//    <DigitalSMEInitiativeLive
//      initiativeCategories={initiativeCategories}
//      collectionid={collectionid}
//      fieldpath={fieldpath}
//      documentid={documentid}
//      disabled={disabled}
//    ></DigitalSMEInitiativeLive>    

//================================================================

//Libraries

import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';

//Contexts
import { GetUser } from '../../Library/GlobalContexts';

//Components

//Functions
import WriteDocument from '../../Library/WriteDocument';

//Images
import successIcon from '../../Components/Images/Icon_Save_Teal.svg';
import pendingIcon from '../../Components/Images/Icon_LoadingFile_Grey.gif';
import errorIcon from '../../Components/Images/Icon_Error_Red.svg';

//CSS

export default function DigitalSMEInitiativeLive({
    initiativeCategories,
    collectionid,
    fieldpath,
    documentid,
    disabled
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Handles the field state --> onload, pending, success, error
    const [fieldStatus, setFieldStatus] = useState('onload');

    // Check if the app is in development environment
    const isDevelopment = process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285';

    // Check if the app is in uat environment
    const isUAT = process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396';

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Handle checkbox change
    const handleCheckboxChange = (event) => {

        setFieldStatus('pending');

        const { name, checked } = event.target;

        let updatedCategories = [...initiativeCategories];

        if (checked) {

            if (!updatedCategories.includes(name)) {
                updatedCategories.push(name);
            }

        } else {
            updatedCategories = updatedCategories.filter(category => category !== name);

        }

        const keys = fieldpath.split('.');
        let results = {};

        if (keys.length === 1) {
            results[keys[0]] = updatedCategories;

        } else if (keys.length === 2) {
            results[keys[0]] = { [keys[1]]: updatedCategories };

        } else if (keys.length === 3) {
            results[keys[0]] = { [keys[1]]: { [keys[2]]: updatedCategories } };

        } else if (keys.length === 4) {
            results[keys[0]] = { [keys[1]]: { [keys[2]]: { [keys[3]]: updatedCategories } } };

        } else {
            console.error('Too many nested fields to handle!');
            return;

        }

        // Add metadata for tracking changes
        results['lastmodifieddate'] = new Date();
        results['lastmodifiedby'] = {
            email: getUser?.emailaddress,
            givenname: getUser?.givenname,
            surname: getUser?.surname
        };


        // Update Firestore with the dynamic field path
        WriteDocument(collectionid, documentid, results, true)
            .then(() => {
                setFieldStatus('success');
            })
            .catch((error) => {
                console.log('error', error);
                setFieldStatus('error');

            })

    };

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='FormComponent-Row'>

            {
                // onload
                fieldStatus === 'onload' ? (
                    <>
                        {/* Question Label */}
                        < div className='flex flex-col'>
                            <label className='font-medium'>2. Does this initiative relate to any of the following? Select all that apply.</label>
                            <p className='mb-0 text-[14px] text-[#424242] italic'>
                                Ensure you have engaged with the right Digital SMEs prior to submitting this
                            </p>
                        </div>

                        {/* Checkboxes */}
                        <div>
                            <div className='flex items-center'>
                                <input
                                    type='checkbox'
                                    name='AI / Gen AI'
                                    checked={initiativeCategories?.includes('AI / Gen AI') || false}
                                    onChange={handleCheckboxChange}
                                    className='mr-2 w-[20px] h-[20px]'
                                    disabled={disabled}
                                ></input>
                                <label htmlFor='AI / Gen AI'>AI / Gen AI</label>
                            </div>

                            <div className='flex items-center'>
                                <input
                                    type='checkbox'
                                    name='Data Strategy / Governance'
                                    checked={initiativeCategories?.includes('Data Strategy / Governance') || false}
                                    onChange={handleCheckboxChange}
                                    className='mr-2 w-[20px] h-[20px]'
                                    disabled={disabled}
                                ></input>
                                <label htmlFor='Data Strategy / Governance'>Data Strategy / Governance</label>
                            </div>

                            <div className='flex items-center'>
                                <input
                                    type='checkbox'
                                    name='Integrations'
                                    checked={initiativeCategories?.includes('Integrations') || false}
                                    onChange={handleCheckboxChange}
                                    className='mr-2 w-[20px] h-[20px]'
                                    disabled={disabled}
                                ></input>
                                <label htmlFor='Integrations'>Integrations</label>
                            </div>

                            <div className='flex items-center'>
                                <input
                                    type='checkbox'
                                    name='Reporting / Analytics / BI'
                                    checked={initiativeCategories?.includes('Reporting / Analytics / BI') || false}
                                    onChange={handleCheckboxChange}
                                    className='mr-2 w-[20px] h-[20px]'
                                    disabled={disabled}
                                ></input>
                                <label htmlFor='Reporting / Analytics / BI'>Reporting / Analytics / BI</label>
                            </div>

                        </div>
                    </>
                ) :
                    // pending
                    fieldStatus === 'pending' ? (
                        <>
                            {/* Question Label */}
                            < div className='flex flex-col'>
                                <label className='font-medium'>2. Does this initiative relate to any of the following? Select all that apply.</label>
                                <p className='mb-0 text-[14px] text-[#424242] italic'>
                                    Ensure you have engaged with the right Digital SMEs prior to submitting this
                                </p>
                            </div>

                            {/* Checkboxes */}
                            <div>
                                <div className='flex items-center'>
                                    <input
                                        type='checkbox'
                                        name='AI / Gen AI'
                                        checked={initiativeCategories?.includes('AI / Gen AI') || false}
                                        onChange={handleCheckboxChange}
                                        className='mr-2 w-[20px] h-[20px]'
                                        disabled={disabled}
                                    ></input>
                                    <label htmlFor='AI / Gen AI'>AI / Gen AI</label>
                                </div>

                                <div className='flex items-center'>
                                    <input
                                        type='checkbox'
                                        name='Data Strategy / Governance'
                                        checked={initiativeCategories?.includes('Data Strategy / Governance') || false}
                                        onChange={handleCheckboxChange}
                                        className='mr-2 w-[20px] h-[20px]'
                                        disabled={disabled}
                                    ></input>
                                    <label htmlFor='Data Strategy / Governance'>Data Strategy / Governance</label>
                                </div>

                                <div className='flex items-center'>
                                    <input
                                        type='checkbox'
                                        name='Integrations'
                                        checked={initiativeCategories?.includes('Integrations') || false}
                                        onChange={handleCheckboxChange}
                                        className='mr-2 w-[20px] h-[20px]'
                                        disabled={disabled}
                                    ></input>
                                    <label htmlFor='Integrations'>Integrations</label>
                                </div>

                                <div className='flex items-center'>
                                    <input
                                        type='checkbox'
                                        name='Reporting / Analytics / BI'
                                        checked={initiativeCategories?.includes('Reporting / Analytics / BI') || false}
                                        onChange={handleCheckboxChange}
                                        className='mr-2 w-[20px] h-[20px]'
                                        disabled={disabled}
                                    ></input>
                                    <label htmlFor='Reporting / Analytics / BI'>Reporting / Analytics / BI</label>
                                    <img className='w-[30px] h-[30px] self-center ml-24' src={pendingIcon} alt='pending-icon'></img>
                                </div>
                            </div>

                        </>
                    ) :

                        // success
                        fieldStatus === 'success' ? (<>
                            {/* Question Label */}
                            < div className='flex flex-col'>
                                <label className='font-medium'>2. Does this initiative relate to any of the following? Select all that apply.</label>
                                <p className='mb-0 text-[14px] text-[#424242] italic'>
                                    Ensure you have engaged with the right Digital SMEs prior to submitting this
                                </p>
                            </div>

                            {/* Checkboxes */}
                            <div>
                                <div className='flex items-center'>
                                    <input
                                        type='checkbox'
                                        name='AI / Gen AI'
                                        checked={initiativeCategories?.includes('AI / Gen AI') || false}
                                        onChange={handleCheckboxChange}
                                        className='mr-2 w-[20px] h-[20px]'
                                        disabled={disabled}
                                    ></input>
                                    <label htmlFor='AI / Gen AI'>AI / Gen AI</label>
                                </div>

                                <div className='flex items-center'>
                                    <input
                                        type='checkbox'
                                        name='Data Strategy / Governance'
                                        checked={initiativeCategories?.includes('Data Strategy / Governance') || false}
                                        onChange={handleCheckboxChange}
                                        className='mr-2 w-[20px] h-[20px]'
                                        disabled={disabled}
                                    ></input>
                                    <label htmlFor='Data Strategy / Governance'>Data Strategy / Governance</label>
                                </div>

                                <div className='flex items-center'>
                                    <input
                                        type='checkbox'
                                        name='Integrations'
                                        checked={initiativeCategories?.includes('Integrations') || false}
                                        onChange={handleCheckboxChange}
                                        className='mr-2 w-[20px] h-[20px]'
                                        disabled={disabled}
                                    ></input>
                                    <label htmlFor='Integrations'>Integrations</label>
                                </div>

                                <div className='flex items-center'>
                                    <input
                                        type='checkbox'
                                        name='Reporting / Analytics / BI'
                                        checked={initiativeCategories?.includes('Reporting / Analytics / BI') || false}
                                        onChange={handleCheckboxChange}
                                        className='mr-2 w-[20px] h-[20px]'
                                        disabled={disabled}
                                    ></input>
                                    <label htmlFor='Reporting / Analytics / BI'>Reporting / Analytics / BI</label>
                                    <img className='LiveField-Success ml-24' src={successIcon} alt='success-icon'></img>
                                </div>

                            </div>
                        </>) :

                            // Catch All --> error
                            (<>
                                {/* Question Label */}
                                < div className='flex flex-col'>
                                    <label className='font-medium'>2. Does this initiative relate to any of the following? Select all that apply.</label>
                                    <p className='mb-0 text-[14px] text-[#424242] italic'>
                                        Ensure you have engaged with the right Digital SMEs prior to submitting this
                                    </p>
                                </div>

                                {/* Checkboxes */}
                                <div>
                                    <div className='flex items-center'>
                                        <input
                                            type='checkbox'
                                            name='AI / Gen AI'
                                            checked={initiativeCategories?.includes('AI / Gen AI') || false}
                                            onChange={handleCheckboxChange}
                                            className='mr-2 w-[20px] h-[20px]'
                                            disabled={disabled}
                                        ></input>
                                        <label htmlFor='AI / Gen AI'>AI / Gen AI</label>
                                    </div>

                                    <div className='flex items-center'>
                                        <input
                                            type='checkbox'
                                            name='Data Strategy / Governance'
                                            checked={initiativeCategories?.includes('Data Strategy / Governance') || false}
                                            onChange={handleCheckboxChange}
                                            className='mr-2 w-[20px] h-[20px]'
                                            disabled={disabled}
                                        ></input>
                                        <label htmlFor='Data Strategy / Governance'>Data Strategy / Governance</label>
                                    </div>

                                    <div className='flex items-center'>
                                        <input
                                            type='checkbox'
                                            name='Integrations'
                                            checked={initiativeCategories?.includes('Integrations') || false}
                                            onChange={handleCheckboxChange}
                                            className='mr-2 w-[20px] h-[20px]'
                                            disabled={disabled}
                                        ></input>
                                        <label htmlFor='Integrations'>Integrations</label>
                                    </div>

                                    <div className='flex items-center'>
                                        <input
                                            type='checkbox'
                                            name='Reporting / Analytics / BI'
                                            checked={initiativeCategories?.includes('Reporting / Analytics / BI') || false}
                                            onChange={handleCheckboxChange}
                                            className='mr-2 w-[20px] h-[20px]'
                                            disabled={disabled}
                                        ></input>
                                        <label htmlFor='Reporting / Analytics / BI'>Reporting / Analytics / BI</label>
                                        <img className='w-[30px] ml-24' src={errorIcon} alt='error-icon'></img>
                                    </div>
                                   <label className='text-[#DE0000]'>Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.</label>
                                </div>
                            </>
                            )
            }

        </div >
    );
};